import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Checkbox,
  ListItemText,
  TextField,
  Autocomplete,
  IconButton,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { format } from "date-fns";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { addHoursToTime, useAuth } from "../sdk";
import { KeyboardBackspace } from "@mui/icons-material";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";

// Styled Components
const StyledContainer = styled(Stack)(({ theme }) => ({
  width: "100%",
  background: "#ffffff",
  padding: "80px 40px 40px 40px",
  border: "none",
  outline: "none",
  height: "100%",
  overflowY: "scroll",
  overflowX: "hidden",
  boxShadow: "1px 4px 10px rgba(48, 111, 188, 0.2)",
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  label: {
    background: "white",
  },
  ".MuiAutocomplete-inputRoot": {
    border: "1px solid rgba(0, 0, 0, 0.23)",
    width: theme.spacing(37.5),
    padding: theme.spacing(0.25),
    overflowX: "auto",
    borderRadius: "5px",
    flexWrap: "nowrap",
    "&::-webkit-scrollbar": {
      display: "none",
    },
    "-ms-overflow-style": "none",
    "scrollbar-width": "none",
    input: {
      minWidth: 100,
    },
    fieldset: {
      display: "none",
    },
  },
  "& .MuiAutocomplete-inputRoot.Mui-focused": {
    border: "2px solid #1976d2",
  },
}));

const FilterContainer = styled(Stack)(({ theme }) => ({
  padding: theme.spacing(2),
  width: "100%",
  flexDirection: "row",
  gap: theme.spacing(2),
  flexWrap: "wrap",
  alignItems: "center",
  justifyContent: "flex-end",
}));

const StatsContainer = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  gap: theme.spacing(2),
  marginBottom: theme.spacing(3),
}));

const StatCard = styled(Stack)(({ theme }) => ({
  display: "flex",
  flexDirection: "row",
  width: "250px",
  gap: theme.spacing(1),
  alignItems: "center",
  padding: theme.spacing(1),
  border: "1px solid #e0e0e0",
  justifyContent: "center",
  borderRadius: 12,
}));

const StatusChip = styled(Chip)(({ status }) => ({
  fontWeight: 400,
  padding: "4px 8px",
  textTransform: "capitalize",
  ...(status === "completed" && {
    backgroundColor: "#10BF101C",
    color: "#115C11",
  }),
  ...(status === "pending" && {
    backgroundColor: "#E13A3A3B",
    color: "#F02323",
  }),
  ...(status === "failed" && {
    backgroundColor: "#f44336",
    color: "white",
  }),
}));

const TableHeaderCell = styled(TableCell)(({ theme }) => ({
  fontWeight: 600,
  backgroundColor: "#f5f5f5",
  color: "#163A90",
  borderBottom: "2px solid #e0e0e0",
}));

const LoadingContainer = styled(Box)({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  minHeight: 400,
});

// Constants
const ITEMS_PER_PAGE = 10;
const STATUS_OPTIONS = [
  { value: "pending", label: "Pending" },
  { value: "completed", label: "Completed" },
];

// Custom Hook for Schedule Data
const useScheduleData = () => {
  const { authFetch } = useAuth();
  const [data, setData] = useState({
    schedule: [],
    totalSchedule: 0,
    completedSchedule: 0,
    totalCount: 0,
  });
  const [hotelOptions, setHotelOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchHotels = useCallback(async () => {
    try {
      const { get } = await authFetch({
        path: "/hotel",
      });
      const response = await get();

      // Safety checks for hotel response
      const hotels = response?.data || response || [];
      const validHotels = Array.isArray(hotels)
        ? hotels.filter((hotel) => hotel?.id && hotel?.name)
        : [];

      setHotelOptions(validHotels);
    } catch (err) {
      console.error("Hotels fetch error:", err);
      setHotelOptions([]);
    }
  }, [authFetch]);

  const fetchScheduleData = useCallback(
    async (params = {}) => {
      try {
        setLoading(true);
        setError(null);

        const queryParams = new URLSearchParams({
          date: params.date || format(new Date(), "yyyy-MM-dd"),
          page: Math.max(0, (params.page || 1) - 1), // Convert 1-based to 0-based
          limit: params.limit || ITEMS_PER_PAGE,
        });

        // Add hotelIds as comma-separated values
        if (params.hotelIds?.length > 0) {
          queryParams.append("hotelIds", params.hotelIds.join(","));
        }

        // Add status filter
        if (params.status) {
          queryParams.append("status", params.status);
        }

        const { get } = await authFetch({
          path: `/schedule-info?${queryParams.toString()}`,
        });

        const response = await get();

        // Comprehensive safety checks for schedule response
        const responseData = response?.data || {};
        const safeData = {
          schedule: Array.isArray(responseData.schedule)
            ? responseData.schedule.filter(
                (item) => item?.hotelId || item?.hotelName
              )
            : [],
          totalSchedule: Number(responseData.totalSchedule) || 0,
          completedSchedule: Number(responseData.completedSchedule) || 0,
          totalCount: Number(responseData.totalCount) || 0,
        };

        setData(safeData);
      } catch (err) {
        const errorMessage =
          err?.message ||
          err?.response?.data?.message ||
          "Failed to fetch schedule data";
        setError(errorMessage);
        console.error("Schedule fetch error:", err);

        // Reset to safe state on error
        setData({
          schedule: [],
          totalSchedule: 0,
          completedSchedule: 0,
          totalCount: 0,
        });
      } finally {
        setLoading(false);
      }
    },
    [authFetch]
  );

  useEffect(() => {
    fetchHotels();
  }, [fetchHotels]);

  return { data, hotelOptions, loading, error, fetchScheduleData };
};

// Sub Components
const StatsCards = React.memo(({ totalSchedule, completedSchedule }) => {
  return (
    <StatsContainer>
      <StatCard>
        <Typography variant="subtitle1" color="textSecondary">
          Total Schedule
        </Typography>
        <Typography variant="subtitle2">{totalSchedule}</Typography>
      </StatCard>

      <StatCard>
        <Typography variant="subtitle1" color="textSecondary">
          Completed Schedule
        </Typography>
        <Typography variant="subtitle2">{completedSchedule}</Typography>
      </StatCard>
    </StatsContainer>
  );
});

const FilterControls = React.memo(
  ({
    selectedDate,
    onDateChange,
    selectedHotels = [],
    onHotelsChange,
    selectedStatus = "",
    onStatusChange,
    hotelOptions = [],
  }) => {
    const hotelMap = useMemo(
      () =>
        hotelOptions.reduce((acc, hotel) => {
          if (hotel?.id) {
            acc[hotel.id] = hotel;
          }
          return acc;
        }, {}),
      [hotelOptions]
    );

    return (
      <FilterContainer>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label="Select Date"
            value={selectedDate || new Date()}
            disableFuture
            inputFormat="dd/MM/yyyy"
            onChange={(newDate) => onDateChange?.(newDate || new Date())}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                sx={{ maxWidth: 150 }}
                error={!selectedDate}
              />
            )}
          />
        </LocalizationProvider>

        <Autocomplete
          multiple
          size="small"
          limitTags={2} // Limit the number of visible tags
          options={hotelOptions}
          value={selectedHotels.map((id) => hotelMap[id]).filter(Boolean)}
          onChange={(event, newValue) =>
            onHotelsChange?.(event, newValue || [])
          }
          getOptionLabel={(option) => option?.name || "Unknown Hotel"}
          isOptionEqualToValue={(option, value) => option?.id === value?.id}
          disableCloseOnSelect
          renderOption={(props, option, { selected }) => (
            <li {...props} key={option?.id}>
              <Checkbox style={{ marginRight: 8 }} checked={selected} />
              <ListItemText primary={option?.name || "Unknown Hotel"} />
            </li>
          )}
          renderInput={(params) => (
            <StyledTextField
              {...params}
              label="Hotels"
              placeholder={selectedHotels.length === 0 ? "All Hotels" : ""}
              size="small"
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                endAdornment: null,
              }}
            />
          )}
          sx={{
            "& .MuiAutocomplete-inputRoot": {
              paddingRight: "10px !important",
            },
          }}
        />

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={selectedStatus}
            onChange={(event) => onStatusChange?.(event)}
            label="Status"
          >
            <MenuItem value="">All Status</MenuItem>
            {STATUS_OPTIONS.map((option) => (
              <MenuItem key={option?.value} value={option?.value || ""}>
                {option?.label || "Unknown Status"}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </FilterContainer>
    );
  }
);

const ScheduleTable = React.memo(({ schedules = [], loading }) => {
  const isDataAvailable = Array.isArray(schedules) && schedules.length > 0;
  if (loading) {
    return (
      <LoadingContainer>
        <CircularProgress size={60} />
      </LoadingContainer>
    );
  }

  return (
    <TableContainer component={Paper} elevation={2}>
      <Table>
        <TableHead>
          <TableRow>
            <TableHeaderCell>Hotel Name</TableHeaderCell>
            <TableHeaderCell>Rateshop Name</TableHeaderCell>
            <TableHeaderCell align="center">Schedule Time</TableHeaderCell>
            <TableHeaderCell align="center">
              Expected Complete Time
            </TableHeaderCell>
            <TableHeaderCell align="center">Status</TableHeaderCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {!isDataAvailable && (
            <TableRow>
              <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                <Typography variant="h6" color="textSecondary">
                  No schedules found for the selected criteria
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {schedules?.map((schedule, index) => (
            <TableRow
              key={`${schedule?.hotelId || "unknown"}-${
                schedule?.scheduleTime || "no-time"
              }-${index}`}
              hover
              sx={{ "&:nth-of-type(odd)": { backgroundColor: "#fafafa" } }}
            >
              <TableCell>
                <Typography variant="body2" fontWeight={500}>
                  {schedule?.hotelName || "Unknown Hotel"}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography variant="body2" fontWeight={500}>
                  {schedule?.rateShopScheduleName || "N/A"}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography
                  variant="body2"
                  fontWeight={500}
                  textAlign={"center"}
                >
                  {schedule?.scheduleTime || "N/A"}
                </Typography>
              </TableCell>
              <TableCell>
                <Typography
                  variant="body2"
                  fontWeight={500}
                  textAlign={"center"}
                >
                  {addHoursToTime(schedule?.scheduleTime, 2) || "N/A"}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <StatusChip
                  label={schedule?.status || "unknown"}
                  status={schedule?.status || "unknown"}
                  size="small"
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
});

// Main Component
export default function ScheduleList({ setPageHeader }) {
  const {
    data,
    hotelOptions,
    loading,
    error,
    fetchScheduleData,
  } = useScheduleData();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedHotels, setSelectedHotels] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [currentPage, setCurrentPage] = useState(1); // Keep UI as 1-based
  const history = useHistory();

  // Fetch data when filters change
  useEffect(() => {
    const params = {
      date: format(selectedDate || new Date(), "yyyy-MM-dd"),
      page: Math.max(1, currentPage), // Pass 1-based page, convert in fetchScheduleData
      limit: ITEMS_PER_PAGE,
      ...(Array.isArray(selectedHotels) &&
        selectedHotels.length > 0 && {
          hotelIds: selectedHotels.filter((id) => id != null),
        }),
      ...(selectedStatus && { status: selectedStatus }),
    };

    fetchScheduleData(params);
  }, [
    selectedDate,
    selectedHotels,
    selectedStatus,
    currentPage,
    fetchScheduleData,
  ]);

  useEffect(() => {
    if (!setPageHeader) return;
    setPageHeader("All Schedule");
    return () => {
      if (!setPageHeader) return;
      setPageHeader("");
    };
  }, [setPageHeader]);

  // Event Handlers with safety checks
  const handleDateChange = useCallback((newDate) => {
    if (newDate && !isNaN(new Date(newDate).getTime())) {
      setSelectedDate(newDate);
      setCurrentPage(1);
    }
  }, []);

  const handleHotelsChange = useCallback((event, newValue) => {
    const safeNewValue = Array.isArray(newValue) ? newValue : [];
    const hotelIds = safeNewValue
      .filter((hotel) => hotel?.id)
      .map((hotel) => hotel.id);
    setSelectedHotels(hotelIds);
    setCurrentPage(1);
  }, []);

  const handleStatusChange = useCallback((event) => {
    const value = event?.target?.value || "";
    setSelectedStatus(value);
    setCurrentPage(1);
  }, []);

  const handlePageChange = useCallback((event, page) => {
    const safePage = Math.max(1, Number(page) || 1);
    setCurrentPage(safePage);
  }, []);

  const handleGoBack = useCallback(() => {
    history.push("/managing_organization");
  }, [history]);

  // Safe pagination calculation
  const totalCount = Number(data?.totalCount) || 0;
  const scheduleLength = Array.isArray(data?.schedule)
    ? data.schedule.length
    : 0;
  const totalPages = Math.max(0, Math.ceil(totalCount / ITEMS_PER_PAGE));
  const shouldShowPagination = totalPages > 1 && scheduleLength > 0;

  return (
    <StyledContainer>
      <Stack direction="row" justifyContent="space-between">
        <IconButton
          disableRipple
          onClick={handleGoBack}
          sx={{
            padding: "0px",
            color: "#808080",
          }}
        >
          <KeyboardBackspace fontSize="large" />
        </IconButton>

        <FilterControls
          selectedDate={selectedDate}
          onDateChange={handleDateChange}
          selectedHotels={selectedHotels}
          onHotelsChange={handleHotelsChange}
          selectedStatus={selectedStatus}
          onStatusChange={handleStatusChange}
          hotelOptions={hotelOptions}
        />
      </Stack>

      <Box mt={3}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <StatsCards
          totalSchedule={data?.totalSchedule || 0}
          completedSchedule={data?.completedSchedule || 0}
        />

        <ScheduleTable schedules={data?.schedule} loading={loading} />

        {shouldShowPagination && (
          <Box display="flex" justifyContent="center" mt={3}>
            <Pagination
              count={totalPages}
              page={Math.min(currentPage, totalPages)}
              onChange={handlePageChange}
              color="primary"
              size="large"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
      </Box>
    </StyledContainer>
  );
}
